import type { Order, Payment } from "../../types";

/** HA-886 — a card payment Pending this long on an order that this till is not collecting is a stranded one. */
export const RECOVER_AFTER_MS = 15_000;

/** HA-886 — about a minute of backoff, then the failure reaches the operator (and recovery takes over). */
const MAX_COMPLETION_RETRIES = 8;

/** HA-886 — how long a failed recovery waits before it is tried again. */
export const RECOVERY_RETRY_MS = 30_000;

export interface CardPaymentToRecover {
	orderId: string;
	paymentId: string;
	externalId: string;
}

/**
 * HA-886 — whether the till's completion call is asked again. A 4xx is the server's definite answer
 * (Stripe says it is not taken, or it is not this payment's): asking again changes nothing. Anything
 * else (a timeout, a dropped connection, a 5xx) says nothing about the payment, and the completion is
 * idempotent, so it is asked again.
 */
export function shouldRetryCompletion(
	failureCount: number,
	error: unknown,
): boolean {
	const status = (error as { response?: { status?: number } } | null)?.response
		?.status;

	if (status !== undefined && status >= 400 && status < 500) return false;

	return failureCount < MAX_COMPLETION_RETRIES;
}

/** HA-886 — the wait before the nth retry of the completion call. */
export function completionRetryDelay(failureCount: number): number {
	return Math.min(1000 * 2 ** failureCount, 8000);
}

/**
 * HA-886 — the card payments on these orders that the server is holding as unfinished and that this
 * till is no longer collecting: Pending, with the intent recorded, past {@link RECOVER_AFTER_MS},
 * not the one being collected now, and not already tried. The server checks each with Stripe, so
 * asking about one that was never taken is refused harmlessly.
 */
export function cardPaymentsToRecover(
	orders: Order[],
	options: {
		activePaymentId?: string;
		now: number;
		attempted: ReadonlySet<string>;
	},
): CardPaymentToRecover[] {
	// HA-554 Part 2 — a Pay Tab card payment's shares are Pending entries on several orders; the payment the server
	// knows is the tab payment, so it is asked about once, under that id.
	const seen = new Set<string>();

	return orders.flatMap((order) =>
		order.payments
			.filter((p) => {
				const id = p.tabPaymentId ?? p.id;
				return (
					p.method === "card" &&
					p.status === "Pending" &&
					!!p.externalId &&
					id !== options.activePaymentId &&
					!options.attempted.has(id) &&
					options.now - Date.parse(p.timestamp) >= RECOVER_AFTER_MS
				);
			})
			.flatMap((p) => {
				const id = p.tabPaymentId ?? p.id;
				if (seen.has(id)) return [];
				seen.add(id);
				return [
					{ orderId: order.id, paymentId: id, externalId: p.externalId! },
				];
			}),
	);
}

/**
 * HA-886 — whether a card payment is held against the bill without the server having confirmed it was
 * taken: Pending past {@link RECOVER_AFTER_MS}. Derived from the payment's own timestamp, so a payment
 * that is simply being collected right now is not labelled.
 */
export function isAwaitingCardConfirmation(
	payment: Pick<Payment, "method" | "status" | "timestamp">,
	now: number,
): boolean {
	return (
		payment.method === "card" &&
		payment.status === "Pending" &&
		now - Date.parse(payment.timestamp) >= RECOVER_AFTER_MS
	);
}
